import React from 'react';
import { Palette } from 'lucide-react';
import { TableConfig, PivotConfig, LossTriangleConfig } from './config-panel/TableConfig';
import {
  BarChartConfig, LineChartConfig, AreaChartConfig, PieChartConfig, ComboConfig,
  ScatterConfig, GaugeConfig, FunnelConfig, TreemapConfig, HeatmapConfig,
  WaterfallConfig, GeoMapConfig, WordCloudConfig,
} from './config-panel/ChartConfigs';
import { TopNSection, PeriodComparisonSection, DrillThroughSection, SeriesSection, PriorYearSection } from './config-panel/sections';
import { useCustomVizTypes } from '@/lib/customVizRegistry';

const TYPE_RENDERERS = {
  table: TableConfig,
  pivot: PivotConfig,
  loss_triangle: LossTriangleConfig,
  bar_chart: BarChartConfig,
  line_chart: LineChartConfig,
  area_chart: AreaChartConfig,
  pie_chart: PieChartConfig,
  combo: ComboConfig,
  scatter: ScatterConfig,
  gauge: GaugeConfig,
  funnel: FunnelConfig,
  treemap: TreemapConfig,
  wordcloud: WordCloudConfig,
  heatmap: HeatmapConfig,
  waterfall: WaterfallConfig,
  geo_map: GeoMapConfig,
};

const NO_TOPN = ['image', 'rich_text', 'shape', 'kpi'];
const NO_PERIOD = ['image', 'rich_text', 'shape', 'kpi', 'loss_triangle'];

export function ConfigPanel({
  vizType, vizConfig, onConfigChange,
  showGrid, setShowGrid, showLegend, setShowLegend,
  refLineValue, setRefLineValue, refLineLabel, setRefLineLabel,
  parameters, allFields, currentVisual, onUpdateVisual, designerMode,
  reportTableSpanPages, setReportTableSpanPages,
  reportTableRepeatHeader, setReportTableRepeatHeader,
  seriesValues = [],
  themeActive = false,
}) {
  const cfg = vizConfig || {};
  const set = (k, v) => onConfigChange({ [k]: v });

  // Built-in renderer wins; otherwise look in custom plugin registry
  const customTypes = useCustomVizTypes();
  const customRenderer = (() => {
    if (TYPE_RENDERERS[vizType]) return null;
    const reg = customTypes.find(c => c.type === vizType);
    return reg?.configPanel || null;
  })();
  const Renderer = TYPE_RENDERERS[vizType] || customRenderer;

  // Allow a plugin to opt-out of TopN / Period / Drill-through
  const customMeta = customTypes.find(c => c.type === vizType)?.meta || {};
  const skipTopN = NO_TOPN.includes(vizType) || customMeta.supportsTopN === false;
  const skipPeriod = NO_PERIOD.includes(vizType) || customMeta.supportsPeriodComparison === false;
  const skipDrill = NO_TOPN.includes(vizType) || customMeta.supportsDrillThrough === false;
  // Series controls (color lock + Top N + Other) apply only to multi-series
  // cartesian charts that have a COLOR/SERIES dimension set.
  const SERIES_CHARTS = ['bar_chart', 'line_chart', 'area_chart', 'combo'];
  const showSeries = SERIES_CHARTS.includes(vizType) && !!currentVisual?.colDim;
  // Prior-year comparison applies to data visuals that can carry a to-date measure.
  const PRIOR_YEAR_TYPES = ['kpi', 'table', 'pivot', 'bar_chart', 'line_chart', 'area_chart', 'pie_chart', 'combo'];
  const showPriorYear = PRIOR_YEAR_TYPES.includes(vizType);

  const sharedProps = {
    cfg, set,
    showGrid, setShowGrid, showLegend, setShowLegend,
    refLineValue, setRefLineValue, refLineLabel, setRefLineLabel,
    parameters, allFields, currentVisual, onUpdateVisual,
    designerMode,
    reportTableSpanPages, setReportTableSpanPages,
    reportTableRepeatHeader, setReportTableRepeatHeader,
    themeActive,
  };

  return (
    <div className="p-3 border-b border-border/30">
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <Palette className="w-2.5 h-2.5" /> {vizType ? vizType.replace('_', ' ').toUpperCase() + ' Config' : 'Format'}
      </p>
      <div className="space-y-1.5">
        {Renderer && <Renderer {...sharedProps} />}
        {showSeries && (
          <SeriesSection cfg={cfg} set={set} seriesValues={seriesValues} />
        )}
        {!skipTopN && (
          <TopNSection cfg={cfg} set={set} currentVisual={currentVisual} allFields={allFields} />
        )}
        {!skipPeriod && (
          <PeriodComparisonSection cfg={cfg} set={set} currentVisual={currentVisual} allFields={allFields} />
        )}
        {!skipDrill && (
          <DrillThroughSection cfg={cfg} set={set} />
        )}
        {showPriorYear && (
          <PriorYearSection cfg={cfg} set={set} />
        )}
      </div>
    </div>
  );
}
